<template>
  <div class="file-uploader" :class="[isEmpty && 'is-empty', fileType, disabled && 'disabled']">
    <img v-if="fileType === FILE_TYPE.IMAGE" class="image-previewer" :src="fileSrc"/>
    <video v-if="fileType === FILE_TYPE.VIDEO" class="video-previewer" :src="fileSrc" controls></video>
    <input
      v-show="false"
      ref="fileInput"
      type="file"
      :accept="acceptFileTypeList.join(',')"
      @change="handleFileChange"
    />
    <div class="uploader-mask">
      <div v-show="isEmpty" class="add-btn" @click="handleAddBtnClick">
        <slot name="uploadIcon">
          <div class="btn-icon">
            <i class="el-icon-circle-plus"></i>
          </div>
        </slot>
        <div class="btn-label">{{ label }}</div>
        <div class=btn-placeholder>{{ placeholder }}</div>
      </div>
      <div v-show="!isEmpty" class="edit-btn-group">
        <div class="edit-btn replace-btn" :title="$t(`common.btn.replace`)" @click="handleReplaceBtnClick">
          <i class="el-icon-edit-outline"></i>
        </div>
        <div class="edit-btn delete-btn" :title="$t(`common.btn.delete`)" @click="handleDeleteBtnClick">
          <i class="el-icon-circle-close"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getFilePath, uploadFile } from '@/utils/qiniu'

export const FILE_TYPE = {
  IMAGE: 'image',
  VIDEO: 'video'
}

export const ACTION_TYPE = {
  ADD: 'add',
  REPLACE: 'replace',
  DELETE: 'delete'
}

export default {
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    fileType: {
      type: String,
      required: true
    },
    value: {
      type: [String, File]
    },
    fileKeyGenerator: {
      type: Function,
      required: true
    },
    acceptFileType: {
      type: Array,
      required: false
    },
    acceptFileSize: {
      type: Number,
      default: 0
    },
    acceptFileSizeMap: {
      type: Object,
      default: () => ({})
    },
    uploadWhenChange: {
      type: Boolean,
      default: false
    },
    label: {
      default: null
    },
    placeholder: {
      default: null
    },
    errorMessages: {
      type: Object,
      default () {
        return {
          acceptFileType: null,
          acceptFileSize: null
        }
      }
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      FILE_TYPE,
      i18nPrefix: 'admin.job.editor',
      loading: false,
      hasUpload: false,
      fileSrc: null,
      fileKey: null,
      currentFile: null,
      currentAction: null,
      defaultFileTypeList: {
        [FILE_TYPE.IMAGE]: ['image/png', 'image/jpeg', 'image/gif', 'image/jpg'],
        [FILE_TYPE.VIDEO]: ['video/mp4']
      }
    }
  },
  computed: {
    isEmpty () {
      return !this.fileSrc
    },
    acceptFileTypeList () {
      let fileTypeList = null
      if (this.acceptFileType) {
        fileTypeList = this.acceptFileType
      } else {
        fileTypeList = this.defaultFileTypeList[this.fileType]
      }
      return fileTypeList
    }
  },
  created () {

  },
  methods: {
    openFileSelector () {
      const fileInput = this.$refs.fileInput
      fileInput.click()
    },
    checkFile (file) {
      if (!this.acceptFileTypeList.includes(file.type)) {
        this.$message.error({
          type: 'error',
          showClose: true,
          message: this.errorMessages.acceptFileType || this.$t(`${this.i18nPrefix}.message.wrongFileType`)
        })
        return true
      }
      const acceptFileSize = this.acceptFileSizeMap[file.type] || this.acceptFileSize
      if (acceptFileSize && file.size > acceptFileSize * 1024 * 1024) {
        this.$message.error({
          type: 'error',
          showClose: true,
          message: this.errorMessages.acceptFileSize || this.$t(`${this.i18nPrefix}.message.wrongFileSize`, { fileSize: acceptFileSize })
        })
        return true
      }
    },
    previewFile () {
      this.loading = true
      const file = this.currentFile
      if (file) {
        const fileTempUrl = window.URL.createObjectURL(file)
        this.fileSrc = fileTempUrl
      } else {
        this.fileSrc = getFilePath(this.fileKey)
      }
      this.loading = false
    },
    async uploadSelectedFile (uploadMode) {
      if (this.disabled) {
        return
      }
      this.loading = true
      return new Promise((resolve, reject) => {
        const file = this.currentFile
        if (!file) {
          resolve(this.fileKey)
          return
        }
        const fileKey = this.fileKeyGenerator(file)
        uploadFile(file, fileKey, true, (resp) => { this.$emit('upload-in-process', resp) }, uploadMode).then((resp) => {
          this.hasUpload = true
          this.$emit('change', fileKey)
          this.$emit('upload-success', resp)
          resolve(fileKey)
        }).catch((error) => {
          this.$emit('upload-error', error)
          reject(error)
        }).finally(() => {
          this.loading = false
        })
      })
    },
    handleFileChange ($event) {
      if (this.disabled) {
        return
      }
      const oldFile = this.currentFile
      const fileInput = this.$refs.fileInput
      const selectedFile = fileInput.files[0]
      const invalid = this.checkFile(selectedFile)
      if (invalid) {
        return
      }
      this.currentFile = selectedFile
      if (this.currentAction === ACTION_TYPE.ADD) {
        this.$emit('file-selected', selectedFile)
      } else {
        this.$emit('file-replaced', { oldFile: oldFile, newFile: selectedFile })
      }
      this.hasUpload = false
      if (this.uploadWhenChange) {
        this.uploadSelectedFile()
      } else {
        this.$emit('change', selectedFile)
      }
    },
    handleAddBtnClick ($event) {
      if (this.disabled) {
        return
      }
      this.currentAction = ACTION_TYPE.ADD
      this.openFileSelector()
    },
    handleReplaceBtnClick ($event) {
      if (this.disabled) {
        return
      }
      this.currentAction = ACTION_TYPE.REPLACE
      this.openFileSelector()
    },
    handleDeleteBtnClick ($event) {
      if (this.disabled) {
        return
      }
      const currentFile = this.currentFile
      this.currentAction = ACTION_TYPE.DELETE
      this.hasUpload = false
      this.loading = false
      this.currentFile = null
      this.fileKey = null
      this.$emit('change', null)
      this.$emit('file-deleted', currentFile)
    }
  },
  watch: {
    value: {
      immediate: true,
      handler () {
        const value = this.value
        if (value && typeof value === 'string') {
          this.fileKey = value
          this.currentFile = null
        } else if (value) {
          this.currentFile = value
        } else {
          this.fileKey = null
          this.currentFile = null
        }
        this.previewFile()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.file-uploader {
  background: #f7f7f7;
  line-height: normal;
  position: relative;

  &.is-empty {
    .uploader-mask {
      height: 100%;

      .add-btn {
        display: flex;
      }
    }
  }

  &.disabled {
    .uploader-mask {
      .add-btn {
        cursor: not-allowed;
      }

      .edit-btn-group {
        .edit-btn {
          cursor: not-allowed;
        }
      }
    }
  }

  &:hover {
    .uploader-mask {

      .edit-btn-group {
        transform: translateY(0);
      }
    }

  }

  .video-previewer, .image-previewer {
    width: 100%;
    height: 100%;
  }

  .video-previewer {
    border: 1px solid transparent;
  }

  .uploader-mask {
    display: flex;
    width: 100%;
    // height: auto;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    position: absolute;
    top: 0;
    left: 0;

    .add-btn {
      display: none;
      width: 100%;
      height: 100%;
      padding: 10px;
      background: #f7f7f7;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      box-sizing: border-box;
      cursor: pointer;

      .btn-icon {
        text-align: center;

        i {
          color: #0096cc;
          font-size: 46px;
        }
      }

      .btn-label {
        text-align: center;
        margin: 10px 0;
      }

      .btn-placeholder {
        text-align: center;
        color: #aaaaaa;
        font-size: 12px;
      }
    }

    .edit-btn-group {
      display: flex;
      width: 100%;
      background: rgba($color: #000000, $alpha: 0.3);
      transition: transform .3s;
      transform: translateY(-100%);
      justify-content: flex-end;

      .edit-btn {
        font-size: 22px;
        margin-right: 10px;
        cursor: pointer;

        &.replace-btn {
          color: #0096cc;
        }

        &.delete-btn {
          color: #fb4646;
        }
      }

    }
  }

}
</style>
